<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <div
      v-if="showHeader"
      class="form-section group collapsable"
      :class="{ 'mb-5': isCollapsable && !isExpanded }">
      <span class="icon"><icon-colored-demographic /></span>
      {{ capitalize($t('messages.audienceTargeting')) }}
      <div
        v-if="isCollapsable"
        class="actions">
        <form-section-expand-collapse
          id="demographic_targeting_expand_collapse"
          :expanded="isExpanded"
          @expanded="isExpanded = true"
          @collapsed="isExpanded = false" />
      </div>
      <div
        v-else-if="!isLineItem"
        class="actions">
        <div
          v-show="isFlexible"
          class="toggle">
          {{ capitalize($t('states.buyerDefinable')) }}
          <toggle-button
            :value="!isOverridable"
            :sync="true"
            class="ml-2 mr-2"
            :disabled="isReadOnly"
            @change="toggleOverridable" />
          {{ capitalize($t('states.predefined')) }}
        </div>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isCollapsable ? isExpanded : !isOverridable"
        class="form-section-content"
        :class="isOverridable ? 'form-section-collapsed' : 'form-section-expanded'">
        <template v-if="didStartInitialization">
          <template v-if="userHasAccessToDemographics && !entityHasUnknownDemographics">
            <div
              id="edit_lineitem_demographics"
              class="form-group row">
              <div
                v-if="userHasAccessToDemographics"
                class="col-12">
                <span
                  v-if="outdatedAudienceWarningMessage"
                  class="alert alert-warning"
                  >{{ capitalize(outdatedAudienceWarningMessage) }}</span
                >
                <div
                  v-if="isLoadingDemographics"
                  class="icon text-center"
                  style="margin: 5rem 0">
                  <loader-small />
                  {{ capitalize($t('messages.loadingAudience')) }}
                </div>
                <feature-selectable-tiles
                  v-else
                  :items="tiles"
                  :entity="entity"
                  :should-display-tables="true"
                  :should-display-icons="true"
                  :should-display-checkmark-on-selected-items="true"
                  label-property-name="name"
                  description-property-name="description"
                  icon-property-name="icon"
                  icon-size="3.2rem"
                  :icon-margin-bottom="iconMarginBottom"
                  icon-margin-right="1rem"
                  :selected-items="selectedTiles"
                  :is-multiselect="canMultiSelectAudienceSegments"
                  :is-read-only="isReadOnly"
                  :is-tile-content-layout-vertical="false"
                  :allow-empty="true"
                  :justify-content-left="true"
                  :min-tile-width="tileWidth"
                  tile-padding="1rem"
                  :tile-margin-bottom="tileMarginBottom"
                  :tile-margin-right="tileMarginRight"
                  separator-height="1rem"
                  :use-auto-width="true"
                  :items-to-not-toggle="[DemographicTile.AUDIENCE_FILES, DemographicTile.IMPORTED_AUDIENCES]"
                  @selection-changed="onSelectedAudienceTilesChanged" />
              </div>
              <!-- OUTSOURCED SEGMENTS -->
              <div
                v-if="outsourcedDemographics.length > 0"
                v-show="isTileSelected(DemographicTile.AUDIENCE_SEGMENTS)"
                class="col-sm-12">
                <form-separator>
                  <template #icon>
                    <icon-colored-audience-geofence-group />
                  </template>
                  SAM Segments
                </form-separator>
                <selectable-tiles
                  :items="outsourcedDemographics"
                  :should-display-labels="true"
                  :should-display-icons="true"
                  :should-display-checkmark-on-selected-items="true"
                  label-property-name="name"
                  description-property-name="description"
                  icon-property-name="icon"
                  :icon-size="iconSize"
                  :icon-margin-bottom="iconMarginBottom"
                  :selected-items="selectedOutsourcedSegments"
                  :is-multiselect="false"
                  :is-read-only="isReadOnly"
                  :allow-empty="true"
                  :justify-content-left="true"
                  :min-tile-width="tileWidth"
                  :tile-width="tileWidth"
                  :tile-padding="tilePadding"
                  :tile-margin-bottom="tileMarginBottom"
                  :tile-margin-right="tileMarginRight"
                  :items-to-not-toggle="[DemographicTile.AUDIENCE_FILES, DemographicTile.IMPORTED_AUDIENCES]"
                  separator-height="1rem"
                  @selection-changed="
                    (val) => {
                      selectedOutsourcedSegments = val;
                    }
                  " />
              </div>

              <!-- DEMO SEGMENTS -->
              <div
                v-else-if="membershipSettings != null && membershipSettings.uiEnableDemoFeatures"
                v-show="isTileSelected(DemographicTile.AUDIENCE_SEGMENTS)"
                class="col-sm-12">
                <form-separator>
                  <template #icon>
                    <icon-colored-audience-geofence-group />
                  </template>
                  {{ capitalize($t('names.predefinedSegment', { n: 2 })) }}
                </form-separator>
                <selectable-tiles
                  :items="predefinedSegments"
                  :should-display-labels="true"
                  :should-display-icons="true"
                  :should-display-checkmark-on-selected-items="true"
                  label-property-name="name"
                  description-property-name="description"
                  icon-property-name="icon"
                  :icon-size="iconSize"
                  :icon-margin-bottom="iconMarginBottom"
                  :selected-items="selectedPredefinedSegments"
                  :is-multiselect="true"
                  :is-read-only="isReadOnly"
                  :allow-empty="true"
                  :justify-content-left="true"
                  :min-tile-width="tileWidth"
                  :tile-width="tileWidth"
                  :tile-padding="tilePadding"
                  :tile-margin-bottom="tileMarginBottom"
                  :tile-margin-right="tileMarginRight"
                  separator-height="1rem"
                  :items-to-not-toggle="[DemographicTile.AUDIENCE_FILES, DemographicTile.IMPORTED_AUDIENCES]"
                  @selection-changed="
                    (val) => {
                      selectedPredefinedSegments = val;
                    }
                  " />
              </div>

              <!-- CUSTOM AUDIENCE -->
              <div
                v-show="!isLoadingDemographics && isTileSelected(DemographicTile.CUSTOM_AUDIENCE)"
                class="form-group row">
                <div class="col-12">
                  <feature-message
                    v-if="isSidekickOrganization"
                    class-name="ml-4"
                    :message="capitalize($t('messages.sidekickTestFeatureDefault'))" />
                  <form-separator>
                    <template #icon>
                      <icon-colored-audience-logic />
                    </template>
                    {{ capitalize($t('names.customAudience', { n: 2 })) }}
                  </form-separator>
                </div>

                <form-label
                  for-i-d="edit_lineitem_custom_demographics"
                  :size="useCompactView ? 12 : 3">
                  {{ capitalize($t('names.audienceLogic', { n: 1 })) }}
                </form-label>

                <div
                  id="edit_lineitem_custom_demographics"
                  :class="useCompactView ? 'col-sm-12' : 'col-sm-8'">
                  <div class="target-custom-audience">
                    <feature-multiselect
                      v-model="selectedCustomDemographic"
                      :entity="entity"
                      selected-feature-key="CUSAUD_HS"
                      :transient="true"
                      :options="customDemographics"
                      :multiple="false"
                      :close-on-select="true"
                      :clear-on-select="true"
                      :hide-selected="false"
                      label="name"
                      group-label="type"
                      track-by="id"
                      :placeholder="DASH"
                      :show-labels="true"
                      :selected-label="useCompactView ? '√' : capitalize($t('states.selected'))"
                      :select-label="useCompactView ? '+' : capitalize($t('actions.select'))"
                      :deselect-label="useCompactView ? '-' : capitalize($t('actions.remove'))"
                      :searchable="true"
                      :allow-empty="true"
                      :disabled="isReadOnly" />
                    <button
                      v-if="selectedCustomDemographic"
                      id="edit_custom_audience"
                      v-tooltip="capitalize($t('actions.editAudienceLogic'))"
                      class="btn btn-primary-hollow btn-sm btn-no-text"
                      @click="editCustomAudience">
                      <icon-edit />
                    </button>
                    <button
                      id="add_custom_audience"
                      v-tooltip="capitalize($t('actions.newAudienceLogic'))"
                      class="btn btn-primary-hollow btn-sm btn-no-text"
                      @click="addCustomAudience">
                      <icon-add />
                    </button>
                  </div>
                  <span
                    v-show="$validator.messageBag.errors.has('customDemographics')"
                    class="help is-danger"
                    >{{ $validator.messageBag.errors.first('customDemographics') }}</span
                  >
                </div>

                <div
                  v-if="selectedCustomAudience && !averageConcentration"
                  class="warning-custom-audience mx-auto mt-4 mb-2">
                  <icon-warning
                    style="width: 24px"
                    class="mr-2" />
                  <strong>
                    {{ capitalize($t('messages.unprocessedCustomAudience')) }}
                  </strong>
                </div>

                <div class="summary-custom-audience">
                  <audience-logic-summary
                    v-if="selectedCustomAudience"
                    :audience-logic="selectedCustomAudience"
                    :show-pane="false"
                    :show-form-separator="true"
                    :center="true"
                    :dark="false"
                    :show-border="false" />
                </div>
              </div>

              <!-- AUDIENCE FILES -->
              <div
                v-if="isTileSelected(DemographicTile.AUDIENCE_FILES)"
                class="form-group row">
                <div class="col-12">
                  <form-separator>
                    <template #icon>
                      <icon-colored-audience-file />
                    </template>
                    {{ capitalize($t('names.audienceFile', { n: 2 })) }}
                  </form-separator>
                </div>

                <form-label
                  for-i-d="edit_lineitem_audience_files"
                  :size="useCompactView ? 12 : 3">
                  {{ capitalize($t('names.audienceFile', { n: 1 })) }}
                </form-label>

                <div
                  id="edit_lineitem_audience_files"
                  :class="useCompactView ? 'col-sm-12' : 'col-sm-8'">
                  <multiselect
                    v-model="selectedAudienceFile"
                    :options="audienceFiles"
                    :multiple="false"
                    :close-on-select="true"
                    :clear-on-select="true"
                    :hide-selected="false"
                    label="name"
                    group-label="type"
                    track-by="id"
                    :placeholder="DASH"
                    :show-labels="true"
                    :selected-label="useCompactView ? '√' : capitalize($t('states.selected'))"
                    :select-label="useCompactView ? '+' : capitalize($t('actions.select'))"
                    :deselect-label="useCompactView ? '-' : capitalize($t('actions.remove'))"
                    :searchable="true"
                    :allow-empty="true"
                    :disabled="isReadOnly" />
                  <span
                    v-show="$validator.messageBag.errors.has('audienceFiles')"
                    class="help is-danger"
                    >{{ $validator.messageBag.errors.first('audienceFiles') }}</span
                  >
                </div>
              </div>

              <!-- IMPORTED AUDIENCES -->
              <div
                v-show="isTileSelected(DemographicTile.IMPORTED_AUDIENCES)"
                class="form-group row">
                <div class="col-12">
                  <form-separator>
                    <template #icon>
                      <icon-colored-imported-audiences />
                    </template>
                    {{ $t('names.importedAudience', { n: 2 }) }}
                  </form-separator>
                </div>

                <form-label
                  for-i-d="edit_lineitem_imported_audiences"
                  :size="useCompactView ? 12 : 3">
                  {{ capitalize($t('names.importedAudience', { n: 1 })) }}
                </form-label>

                <div
                  id="edit_lineitem_imported_audiences"
                  :class="useCompactView ? 'col-sm-12' : 'col-sm-8'">
                  <multiselect
                    v-model="selectedImportedAudienceSegments"
                    :options="importedAudiences"
                    :multiple="false"
                    :close-on-select="true"
                    :clear-on-select="true"
                    :hide-selected="false"
                    label="name"
                    group-label="type"
                    track-by="id"
                    :placeholder="DASH"
                    :show-labels="true"
                    :selected-label="useCompactView ? '√' : capitalize($t('states.selected'))"
                    :select-label="useCompactView ? '+' : capitalize($t('actions.select'))"
                    :deselect-label="useCompactView ? '-' : capitalize($t('actions.remove'))"
                    :searchable="true"
                    :allow-empty="true"
                    :disabled="isReadOnly" />
                </div>
              </div>

              <!-- PRIZM SEGMENTS-->
              <div
                v-if="isTileSelected(DemographicTile.PRIZM_SEGMENTS)"
                class="col-sm-12">
                <form-separator>
                  <template #icon>
                    <icon-colored-account-home />
                  </template>
                  {{ $t('names.prizmSegment', { n: 2 }) }}
                </form-separator>
                <prizm-segments
                  v-if="!selectedPrizmSegments.length"
                  @on-prizm-segment-selected="onPrizmSegmentSelected" />

                <prizm-segments
                  v-for="segment in selectedPrizmSegments"
                  v-else
                  :key="segment.id"
                  :selected-demographic-id="segment.id"
                  @on-prizm-segment-selected="onPrizmSegmentSelected" />
              </div>

              <!-- BASE DEMOGRAPHICS -->
              <div
                v-if="demographics && isTileSelected(DemographicTile.BASE_DEMOGRAPHICS)"
                class="col-sm-12">
                <form-separator>
                  <template #icon>
                    <icon-colored-demographic />
                  </template>
                  {{ capitalize($t('names.demographic', { n: 2 })) }}
                </form-separator>
                <demographic-segments
                  ref="censusDemographics"
                  :demographics="baseDemographics"
                  :default-gender="null"
                  :selected-demographics="selectedBaseDemographics"
                  :is-read-only="isReadOnly"
                  :is-census="true"
                  @on-change="onBaseDemographicsSelected"
                  @update-beta-demos="onUpdateBetaDemos" />
              </div>
            </div>

            <!-- LIFESIGHT DEMOGRAPHICS -->
            <div
              v-if="isTileSelected(DemographicTile.LIFESIGHT_DEMOGRAPHICS)"
              class="col-sm-12">
              <lifesight-demographics-rule
                :audience-rule="{ lifesightSegments: selectedLifesightSegments }"
                :is-editing="true"
                @on-lifesight-demographic-selected="onLifesightDemographicSelected" />
            </div>

            <!-- SCENTRE PREDEFINED SEGMENTS -->
            <div
              v-if="isTileSelected(DemographicTile.SCENTRE_PREDEFINED_SEGMENTS)"
              class="col-sm-12">
              <form-separator>
                <template #icon>
                  <icon-colored-audience-geofence-group />
                </template>
                {{ capitalize($t('names.predefinedSegment', { n: 2 })) }}
              </form-separator>
              <scentre-predefined-segments
                :is-read-only="false"
                :selected-demographic="selectedScentrePredefinedSegments"
                :demographics="scentrePredefinedSegments"
                @on-change="onScentrePredefinedSegmentChange" />
            </div>
          </template>
          <template v-else>
            <div class="well dark">{{ capitalize($t('names.customAudience', { n: 1 })) }}</div>
          </template>
        </template>
        <template v-else>
          <no-records-table
            :icon="markRaw(IconComplexAudience)"
            :header-text="$t('messages.addAnAudience')"
            :details-text="$t('messages.addAnAudienceBody')"
            :button-name="'defineYourAudience'"
            :action-text="capitalize($t('actions.defineYourAudience'))"
            :is-primary="false"
            :action-handler="initialize" />
        </template>
        <slot />
        <!-- MINIMUM AUDIENCE CONCENTRATION -->
        <div
          v-if="canShowMinAudienceConcentration"
          class="col-sm-12">
          <audience-concentration
            :entity="entity"
            :column-width="8"
            :average-concentration="averageConcentration"
            :show-min-concentration="showMinAudienceConcentration"
            :show-hourly-concentration="canShowHourlyAudienceConcentration"
            :is-disabled="!selectedTiles?.length || inheritedDemographics?.length > 0"
            :show-apply-concentration="!!averageConcentration"
            @changed="(val) => $emit('minConcentrationChanged', val)"
            @apply="applyConcentrationChange" />
        </div>
      </div>
    </transition>
    <!-- Custom Audience Modal -->
    <modal
      :use-full-width="true"
      :use-full-height="true"
      :no-padding="true"
      :visible="(selectedCustomAudience || isCustomAudienceModalForNewAudience) && isCustomAudienceModalShowing"
      :title="
        capitalize($t(isCustomAudienceModalForNewAudience ? 'actions.newAudienceLogic' : 'actions.editAudienceLogic'))
      "
      :button1-visible="false">
      <template #modal-after-form>
        <div class="flex-v-full">
          <EditAudienceLogic
            v-if="selectedCustomAudience || isCustomAudienceModalForNewAudience"
            :audience-logic-id="isCustomAudienceModalForNewAudience ? 'new' : selectedCustomAudience.id"
            :is-modal="true"
            @complete="updateCustomAudience" />
        </div>
      </template>
    </modal>
  </div>
</template>

<script type="text/babel">
  import 'vue-slider-component/theme/antd.css';

  import { capitalize, markRaw } from 'vue';
  import { Multiselect } from 'vue-multiselect';
  import { mapActions, mapGetters, mapState } from 'vuex';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import {
    FormLabel,
    FormSectionExpandCollapse,
    FormSeparator,
    Modal,
    SelectableTiles,
  } from '@/common/components/controls';
  import FeatureMultiselect from '@/common/components/controls/FeatureMultiselect.vue';
  import FeatureSelectableTiles from '@/common/components/controls/FeatureSelectableTiles.vue';
  import LoaderSmall from '@/common/components/controls/LoaderSmall';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import FeatureMessage from '@/common/components/generic-messages/FeatureMessage.vue';
  import DemographicSegments from '@/common/components/targeting/DemographicSegments.vue';
  import DemographicUtils from '@/common/components/targeting/DemographicUtils';
  import KantarUtils from '@/common/components/targeting/kantar/KantarUtils';
  import ScentrePredefinedSegments from '@/common/components/targeting/scentre/ScentrePredefinedSegments.vue';
  import ScentreUtils from '@/common/components/targeting/scentre/ScentreUtils';
  import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
  import { DataProviderCodes, isCensusDataProvider, isLifesightDataProvider } from '@/common/enums/data-provider-codes';
  import { DemographicTile } from '@/common/enums/DemographicTile';
  import DemographicType from '@/common/enums/DemographicType.enum';
  import AudienceSegmentMapping from '@/common/icons/audience-segments/OutsourcedAudienceSegmentMapping';
  import {
    IconColoredAccountHome,
    IconColoredAudienceFile,
    IconColoredAudienceGeofenceGroup,
    IconColoredAudienceLogic,
    IconColoredDemographic,
    IconColoredImportedAudiences,
  } from '@/common/icons/colored';
  import { IconComplexAudience } from '@/common/icons/complex';
  import { IconAdd, IconEdit, IconWarning } from '@/common/icons/monochrome';
  import LineItem from '@/common/models/lineitem/LineItem';
  import LineItemProperty from '@/common/models/lineitem/LineItemProperty';
  import Package from '@/common/models/package/Package';
  import PackageProperty from '@/common/models/package/PackageProperty';
  import Plan from '@/common/models/plan/Plan';
  import PlanProperty from '@/common/models/plan/PlanProperty';
  import { Filter, FilterCollection, FilterComparisonOperator } from '@/common/models/query-params';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import GlobalEvents from '@/GlobalEvents';
  import { DASH, environment, i, last, orderBy } from '@/globals';
  import AudienceConcentration from '@/org-app/components/audienceConcentration/AudienceConcentration.vue';
  import { EditAudienceLogic } from '@/org-app/components/audienceLogics';
  import AudienceLogicSummary from '@/org-app/components/audienceLogics/AudienceLogicSummary.vue';
  import LifesightDemographicsRule from '@/org-app/components/audienceLogics/LifesightDemographicsRule';
  import PrizmSegments from '@/org-app/components/audienceLogics/PrizmSegments';
  import { useDemographics } from '@/org-app/services/tanstack/queries';
  import { queryKeys } from '@/org-app/services/tanstack/queryKeys';
  import { removeQueriesWithExtraParams, waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import i18n from '@/vueI18n';

  import PredefinedSegments from './PredefinedSegments';

  export default {
    components: {
      AudienceConcentration,
      FeatureMultiselect,
      FeatureSelectableTiles,
      AudienceLogicSummary,
      EditAudienceLogic,
      Modal,
      FormLabel,
      FormSectionExpandCollapse,
      FormSeparator,
      IconAdd,
      IconColoredAccountHome,
      IconColoredAudienceFile,
      IconColoredAudienceGeofenceGroup,
      IconColoredAudienceLogic,
      IconColoredDemographic,
      IconColoredImportedAudiences,
      IconEdit,
      IconWarning,
      LifesightDemographicsRule,
      LoaderSmall,
      Multiselect,
      PrizmSegments,
      ScentrePredefinedSegments,
      DemographicSegments,
      SelectableTiles,
      NoRecordsTable,
      FeatureMessage,
    },
    props: {
      averageConcentration: {
        type: Number,
        default: 0,
      },
      entity: {
        type: Object,
        default: null,
      },
      useCompactView: {
        type: Boolean,
        default: false,
      },
      isFlexible: {
        type: Boolean,
        default: false,
      },
      isOverridable: {
        type: Boolean,
        default: false,
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
      showHeader: {
        type: Boolean,
        default: true,
      },
      iconSize: {
        type: String,
        default: '18rem',
      },
      iconMarginBottom: {
        type: String,
        default: '0',
      },
      tileWidth: {
        type: String,
        default: '20rem',
      },
      tilePadding: {
        type: String,
        default: '1rem 1rem 2rem',
      },
      tileMarginBottom: {
        type: String,
        default: '1.6rem',
      },
      tileMarginRight: {
        type: String,
        default: '1.2rem',
      },
      separatorHeight: {
        type: String,
        default: '1rem',
      },
      enableJourneys: {
        type: Boolean,
        default: false,
      },
      enableAudienceFiles: {
        type: Boolean,
        default: false,
      },
      isCollapsable: {
        type: Boolean,
        default: false,
      },
      showMinAudienceConcentration: {
        type: Boolean,
        default: true,
      },
      showHourlyAudienceConcentration: {
        type: Boolean,
        default: false,
      },
      /**
       * In the case where the parent entity does not have any demographics selected,
       * this will defer the initialization until the user clicks the toggle to enable
       * demographic targeting. This is meant to help with performance.
       */
      shouldDeferInitialization: {
        type: Boolean,
        default: false,
      },
      inheritedDemographics: {
        type: Array,
        default: () => [],
      },
    },
    emits: [
      'changed',
      'minConcentrationApplied',
      'propertiesChanged',
      'toggled',
      'minConcentrationChanged',
      'selectUnits',
    ],
    data() {
      return {
        canMultiSelectAudienceSegments: false,
        dataProviders: [],
        DASH,
        DemographicTile,
        didStartInitialization: false,
        IconComplexAudience,
        isCustomAudienceModalForNewAudience: false,
        isCustomAudienceModalShowing: false,
        isExpanded: true,
        isLoadingDemographics: true,
        isReady: false,
        isSelectedCustomAudienceOutOfDate: false,
        isToggled: false,
        originalDemographicIDs: [],
        originalProperties: [],
        outdatedAudienceWarningMessage: null,
        outsourcedDemographics: null,
        predefinedSegments: PredefinedSegments.options(),
        selectedAudienceFile: null,
        selectedCustomAudience: null,
        selectedCustomDemographic: null,
        selectedGender: null,
        selectedImportedAudienceSegments: null,
        selectedJourneyAnalysis: null,
        selectedKantarCombination: [],
        selectedLifesightSegments: [],
        selectedOutsourcedSegments: [],
        selectedPredefinedSegments: [],
        selectedPrizmSegments: [],
        selectedBaseDemographics: [],
        selectedScentrePredefinedSegments: [],
        selectedScentreCombination: [],
        selectedTiles: [],
        userHasAccessToDemographics: true,
        useTilesAndSliderForGenderAndAge: true,
        customAudienceModalId: null,
        entityHasUnknownDemographics: false,
        demographicsQuery: useDemographics(),
        loadingJourneyUnits: false,
      };
    },
    computed: {
      IconWarning() {
        return IconWarning;
      },
      ...mapGetters(['membershipSettings']),
      ...mapGetters('orgApp', ['isSidekickOrganization']),
      ...mapState('orgApp', {
        organization: (state) => state.organization,
        organizationSettings: (state) => state.organizationSettings,
      }),
      canShowMinAudienceConcentration() {
        return (
          (this.showMinAudienceConcentration || this.showHourlyAudienceConcentration) &&
          this.didStartInitialization &&
          !this.isLoadingDemographics &&
          !!this.entity.demographicIDs?.length
        );
      },
      canShowHourlyAudienceConcentration() {
        return (
          this.organizationSettings?.uiEnableAudienceConcentrationDetails &&
          this.showHourlyAudienceConcentration &&
          !!this.entity.demographicIDs?.length
        );
      },
      canChooseCustomDemographics() {
        return (
          this.organization.organizationFeatures.customAudiences && this.organizationSettings?.uiEnableAudienceLogics
        );
      },
      audienceFiles() {
        if (this.demographics == null) {
          return [];
        }
        return this.demographics.filter((x) => x.type === 'AUDIENCE-FILE').sort((a, b) => a.name.localeCompare(b.name));
      },
      customDemographics() {
        if (this.demographics == null) {
          return [];
        }

        /**
         * TODO: Remove the following if statement after about 2 months of being in PROD. Temporary solution for Publicis US in prod.
         * Ticket number to remove: TOM-2959 (https://hivestack.atlassian.net/browse/TOM-2959?atlOrigin=eyJpIjoiOTliMDRkZDljNzBjNGMzOWE5ZmI2MzU0MDhjYmQ1Y2MiLCJwIjoiaiJ9)
         */
        if (environment === 'production' && this.organization.id === 345) {
          return AudienceSegmentUtils.getSortedCustomAudiencesForPublicisProd(this.demographics);
        }
        // lifesight demographics precrynched segment have type CUS from fee perspective but is not really a custom audience and shouldn't be displayed as such
        // see https://hivestack.atlassian.net/browse/TOM-1836 for more info
        return AudienceSegmentUtils.getSortedCustomAudiences(this.demographics);
      },
      prizmSegments() {
        return this.demographics?.filter((demo) => demo.code?.startsWith(AudienceSegmentUtils.prizmId));
      },
      tiles() {
        const tiles = [];
        if (this.outsourcedDemographics.length > 0) {
          tiles.push({
            id: DemographicTile.AUDIENCE_SEGMENTS,
            name: 'SAM Segments', // capitalize(i18n.global.t('names.predefinedSegment', { n: 2 })),
          });
        }
        if (this.membershipSettings?.uiEnableBetaFeatures) {
          if (this.outsourcedDemographics.length === 0) {
            tiles.push({
              id: DemographicTile.AUDIENCE_SEGMENTS,
              name: capitalize(i18n.global.t('names.predefinedSegment', { n: 2 })),
              icon: markRaw(IconColoredAudienceGeofenceGroup),
            });
          }
        }
        if (this.baseDemographics.length) {
          tiles.push({
            id: DemographicTile.BASE_DEMOGRAPHICS,
            name: capitalize(i18n.global.t('names.demographic', { n: 2 })),
            icon: markRaw(IconColoredDemographic),
          });
        }
        if (this.canChooseCustomDemographics) {
          tiles.push({
            id: DemographicTile.CUSTOM_AUDIENCE,
            name: capitalize(i18n.global.t('names.customAudience', { n: 2 })),
            icon: markRaw(IconColoredAudienceLogic),
          });
        }
        if (this.enableAudienceFiles) {
          tiles.push({
            id: DemographicTile.AUDIENCE_FILES,
            name: capitalize(i18n.global.t('names.audienceFile', { n: 2 })),
            icon: markRaw(IconColoredAudienceFile),
          });
        }

        if (this.importedAudiences?.length) {
          tiles.push({
            id: DemographicTile.IMPORTED_AUDIENCES,
            name: capitalize(i18n.global.t('names.importedAudience', { n: 2 })),
            icon: IconColoredImportedAudiences,
          });
        }

        if (this.showLifesightPredefinedDemographics) {
          tiles.push({
            id: DemographicTile.LIFESIGHT_DEMOGRAPHICS,
            name: capitalize(i18n.global.t('names.demographic', { n: 2 })),
            icon: IconColoredDemographic,
          });
        }

        if (this.canUsePrizmSegments && this.prizmSegments?.length) {
          tiles.push({
            id: DemographicTile.PRIZM_SEGMENTS,
            name: capitalize(i18n.global.t('names.prizmSegment', { n: 2 })),
            icon: IconColoredAccountHome,
          });
        }

        if (this.scentrePredefinedSegments?.length) {
          tiles.push({
            id: DemographicTile.SCENTRE_PREDEFINED_SEGMENTS,
            name: capitalize(i18n.global.t('names.predefinedSegment', { n: 2 })),
            icon: IconColoredAudienceGeofenceGroup,
          });
        }

        return tiles;
      },
      isLineItem() {
        return this.entity instanceof LineItem;
      },
      isPackage() {
        return this.entity instanceof Package;
      },
      isPlan() {
        return this.entity instanceof Plan;
      },
      isTileSelected() {
        return (tileId) => this.selectedTiles.length > 0 && this.selectedTiles.some((tile) => tile.id === tileId);
      },
      entityProperties() {
        return this.entity.properties;
      },
      importedAudiences() {
        // imported audiences are outsourced segment that are not mapped inside the AudienceSegmentMapping
        return (
          orderBy(
            this.demographics
              ?.filter((demo) => demo.type === 'OUTSOURCED')
              .filter((x) => AudienceSegmentMapping.map((a) => a.code).indexOf(x.code) === -1),
            'name',
          ) || []
        );
      },
      canUsePrizmSegments() {
        return this.organizationSettings?.uiEnableEnvironicsPrizm;
      },
      showLifesightPredefinedDemographics() {
        // in edit mode, if we previously created a LI/Plan/etc with a Lifesight Demographics despite Census data being available, show Lifesight Demographics
        if (this.selectedLifesightSegments?.length) return true;
        // Prioritize base demographics, if available (e.g. scentre kantar environics nexiga etc)
        if (this.baseDemographics.length) return false;

        // show Lifesight demographics only if we have data
        return this.demographics?.filter(
          (d) =>
            AudienceSegmentUtils.isDemographicALifesightPrecrunchedSegment(d) &&
            (d.subType === DemographicType.Age || d.subType === DemographicType.Gender),
        )?.length;
      },
      baseDataProvider() {
        const dataProviderCode = this.organizationSettings?.baseDataProviderCode;
        return dataProviderCode ? this.dataProviders.find((dp) => dp.code === dataProviderCode) : undefined;
      },
      censusDataProviders() {
        return this.dataProviders.filter(isCensusDataProvider);
      },
      baseDemographics() {
        if (this.demographics) {
          return this.demographics.filter((demo) => {
            // Should have a dataProviderId that matches the base data provider
            if (demo.dataProviderId && demo.dataProviderId === this.baseDataProvider?.id) {
              return true;
            }
            // Otherwise include previously selected base demographics
            return this.entity.demographicIDs.includes(demo.id) && DemographicUtils.isBaseDemographic(demo);
          });
        }
        return [];
      },
      scentreDemographicsCombinations() {
        return !this.demographics?.length ? [] : ScentreUtils.getCombinations(this.demographics, this.organization);
      },
      kantarDemographicsCombinations() {
        return !this.demographics?.length ? [] : KantarUtils.getCombinations(this.demographics);
      },
      scentrePredefinedSegments() {
        return !this.demographics?.length
          ? []
          : ScentreUtils.getPrimaryPredefinedSegments(this.demographics, this.organization);
      },
      kantarDataProvider() {
        return this.dataProviders.find((dataProvider) => dataProvider.code === DataProviderCodes.KANTAR);
      },
      lifesightDataProvider() {
        return this.dataProviders.find(isLifesightDataProvider);
      },
      hasBetaAudience() {
        return this.entityProperties != null && this.entityProperties.some((x) => x.key === 'beta-audience');
      },
      demographics() {
        return this.demographicsQuery?.items || [];
      },
    },
    watch: {
      selectedBaseDemographics(val) {
        if (this.entity instanceof LineItem) {
          if (val.length) {
            const demo = val[0];
            if (this.censusDataProviders.some((dp) => demo.dataProviderId === dp.id)) {
              this.updateLineItemAddFeatureKey({
                lineItem: this.entity,
                value: [{ key: 'DEMOAUD_HS', transient: true }],
              });
            }
          } else {
            this.updateLineItemRemoveFeatureKey({
              lineItem: this.entity,
              value: [{ key: 'DEMOAUD_HS', transient: true }],
            });
          }
        }
      },
      async inheritedDemographics(val) {
        const hasInheritedDemographics = val.length > 0;

        if (!this.isExpanded && hasInheritedDemographics) {
          this.isExpanded = true;
        }

        if (
          (hasInheritedDemographics && this.userHasAccessToDemographics) ||
          (!hasInheritedDemographics && !this.userHasAccessToDemographics)
        ) {
          this.selectedTiles = [];
          this.userHasAccessToDemographics = !hasInheritedDemographics;
        }
      },
      selectedOutsourcedSegments() {
        if (this.isReady) {
          this.updateEntityDemographicIDs(this.selectedOutsourcedSegments.map((x) => x.id));
          this.fireChangeEvent();
        }
      },
      selectedPredefinedSegments() {
        this.updateBetaDemos();
        if (this.isReady) {
          this.updateEntityDemographicIDs([]);
          this.fireChangeEvent();
        }
      },
      selectedTiles(newValue, oldValue) {
        if (!this.isReady || this.selectedTiles == null || this.selectedTiles.length === 0) {
          this.updateEntityDemographicIDs([]);
          this.onBaseDemographicsSelected([]);
          this.selectedCustomDemographic = null;
          this.entityHasUnknownDemographics = false;
          this.selectedJourneyAnalysis = null;
          this.$emit('changed', {
            type: [],
            desc: DASH,
          });
          return;
        }

        if (
          this.selectedTiles.some((tile) => tile.id === DemographicTile.AUDIENCE_SEGMENTS) ||
          this.selectedTiles.some((tile) => tile.id === DemographicTile.IMPORTED_AUDIENCES)
        ) {
          this.updateOutsourcedSegments();
        }

        // Clear base demographics when unselected
        if (
          oldValue.find((tile) => tile.id === DemographicTile.BASE_DEMOGRAPHICS) &&
          !this.isTileSelected(DemographicTile.BASE_DEMOGRAPHICS)
        ) {
          this.onBaseDemographicsSelected([]);
        }

        // Clear custom audience when unselected
        if (!this.isTileSelected(DemographicTile.CUSTOM_AUDIENCE)) {
          this.selectedCustomDemographic = null;
        }

        this.updateBetaDemos();
        this.fireChangeEvent();
      },
      selectedCustomDemographic(val) {
        const existingCustomDemographicIDs = this.demographics
          .filter((x) => x.type === DemographicType.Custom && this.entity.demographicIDs.includes(x.id))
          .map((x) => x.id);
        let demographicIDs = [];
        if (val?.id != null) {
          demographicIDs = [
            ...new Set([
              ...this.entity.demographicIDs.filter((x) => !existingCustomDemographicIDs.includes(x)),
              val.id,
            ]),
          ];
        } else {
          demographicIDs = this.entity.demographicIDs.filter((x) => !existingCustomDemographicIDs.includes(x));
        }
        this.updateEntityDemographicIDs(demographicIDs);
        this.fireChangeEvent();
        this.checkIfCustomAudienceIsOutOfDate(val);
        this.getCustomAudience(val).then((data) => {
          this.selectedCustomAudience = data;
        });
      },
      selectedAudienceFile(val) {
        this.updateEntityDemographicIDs(val != null ? [val.id] : []);
        this.fireChangeEvent();
      },
      selectedImportedAudienceSegments(val) {
        this.updateEntityDemographicIDs(val != null ? [val.id] : []);
        this.fireChangeEvent();
      },
      isReadOnly(val) {
        if (val) {
          // Some watchers are triggered before isReadOnly being accessible
          // and it messes up the non-overridable properties that must match with package (#CAM-3095)
          this.updateEntityDemographicIDs(this.originalDemographicIDs, true);
          this.updateEntityProperties(this.originalProperties, true);
        }
      },
      isExpanded(val) {
        if (val && !this.didStartInitialization) {
          this.initialize();
        }
      },
      demographics: {
        handler(val) {
          if (
            this.outsourcedDemographics != null &&
            this.demographics.filter((x) => x.type === 'OUTSOURCED').length !== this.outsourcedDemographics.length
          ) {
            this.outsourcedDemographics = null;
          }
          if (this.outsourcedDemographics == null) {
            if (val != null) {
              const demographicsMatchingOutsourcedSegments = val
                .filter((x) => x.type === 'OUTSOURCED')
                .filter((x) => AudienceSegmentMapping.some((asm) => asm.code === x.code));
              this.outsourcedDemographics = demographicsMatchingOutsourcedSegments
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((x) => ({
                  ...x,
                  icon: AudienceSegmentMapping.find((s) => s.code === x.code).icon,
                }));
            } else {
              this.outsourcedDemographics = [];
            }
          }
        },
        immediate: true,
      },
    },
    beforeMount() {
      removeQueriesWithExtraParams([queryKeys.demographics]);
    },
    async mounted() {
      this.setOriginalDemographics();
      if (this.shouldDeferInitialization && this.originalDemographicIDs.length === 0 && !this.hasBetaAudience) {
        this.isExpanded = false;
        // Deferring initialization until the toggle is clicked.
      } else {
        this.isExpanded = true;
        await this.initialize();
      }
    },
    methods: {
      i,
      markRaw,
      capitalize,
      ...mapActions([
        'updateLineItemProperties',
        'updatePackageProperties',
        'updatePlanProperties',
        'updateLineItemDemographicIDs',
        'updatePackageDemographicIDs',
        'updatePlanDemographicIDs',
        'updateLineItemAddFeatureKey',
        'updateLineItemRemoveFeatureKey',
      ]),
      ...mapActions('orgApp', [
        'getAudienceLogics',
        'getAllLifesightSegments',
        'getAllDataProviders',
        'getAudienceRuleCustomGroups',
      ]),
      applyConcentrationChange(val) {
        this.$emit('minConcentrationApplied', val);
      },
      async getCustomAudience(demographic) {
        if (!demographic?.id) return null;

        const filter = new FilterCollection([
          new Filter('demographic_id', [demographic.id], FilterComparisonOperator.EQUAL),
        ]);
        const results = await this.queryParams(1, filter, ['audiencerules']).run(this.getAudienceLogics);

        return results?.items[0];
      },
      async checkIfCustomAudienceIsOutOfDate(demographic) {
        this.isSelectedCustomAudienceOutOfDate = false;
        if (!demographic?.id) return;

        const audienceLogic = this.getCustomAudience(demographic);
        if (!audienceLogic?.audienceRules?.length) return;

        const segmentRules = audienceLogic.audienceRules.filter((rule) => rule.mode === AudienceRuleModes.Segments);

        if (!segmentRules?.length) return;

        if (this.lifesightDataProvider) {
          const customGroupIds = segmentRules.map((rule) => rule.customGroupID);
          const groups = (
            await this.queryParams(
              customGroupIds.length,
              new FilterCollection([new Filter('customgroup_id', customGroupIds, FilterComparisonOperator.EQUAL)]),
            ).run(this.getAudienceRuleCustomGroups)
          )?.items;
          const externalIds = groups.map((group) => AudienceSegmentUtils.getSegmentIdFromS3Path(group.s3Path));
          const queryParams = this.queryParams(
            externalIds.length,
            new FilterCollection([new Filter('external_id', externalIds, FilterComparisonOperator.EQUAL)]),
          );
          queryParams.customQueryParams.dataProviderId = this.lifesightDataProvider.id;
          const segments = (
            await this.queryParams(
              externalIds.length,
              new FilterCollection([new Filter('external_id', externalIds, FilterComparisonOperator.EQUAL)]),
            ).run(this.getAllLifesightSegments)
          )?.items;
          const archivedSegments = segments.filter((segment) => !segment.enabled);

          this.isSelectedCustomAudienceOutOfDate = archivedSegments.length > 0;
        } else {
          this.isSelectedCustomAudienceOutOfDate = false;
        }
      },
      queryParams(top = 1, filterCollection, expand = null) {
        return new QueryParams(top, 0, filterCollection, null, null, expand);
      },
      updateEntityDemographicIDs(value, forceToUpdate = false) {
        if (this.isReadOnly && !forceToUpdate) return;
        if (this.isLineItem) {
          this.updateLineItemDemographicIDs({
            lineItem: this.entity,
            value: this.inheritedDemographics.length > 0 ? this.inheritedDemographics : value,
          });
        } else if (this.isPackage) {
          this.updatePackageDemographicIDs({
            package: this.entity,
            value,
          });
        } else if (this.isPlan) {
          this.updatePlanDemographicIDs({
            plan: this.entity,
            value: this.inheritedDemographics.length > 0 ? this.inheritedDemographics : value,
          });
        }
      },
      updateEntityProperties(value, forceToUpdate = false) {
        if (this.isReadOnly && !forceToUpdate) return;
        if (this.isLineItem) {
          this.updateLineItemProperties({
            lineItem: this.entity,
            value,
          });
        } else if (this.isPackage) {
          this.updatePackageProperties({
            package: this.entity,
            value,
          });
        } else if (this.isPlan) {
          // For beta demos, we only use Predefined segments and Age/Gender/Income
          if (
            this.isTileSelected(DemographicTile.AUDIENCE_SEGMENTS) ||
            this.isTileSelected(DemographicTile.BASE_DEMOGRAPHICS)
          ) {
            this.updatePlanProperties({
              plan: this.entity,
              value,
            });
          }
        }
        this.$emit('propertiesChanged', value);
      },
      fireChangeEvent() {
        if (!this.isReady) {
          return;
        }
        if (this.selectedTiles == null || this.selectedTiles.length === 0) {
          return;
        }

        const isBaseDataProviderScentre = this.baseDataProvider?.code === DataProviderCodes.SCENTRE;
        if (
          this.isTileSelected(DemographicTile.SCENTRE_PREDEFINED_SEGMENTS) ||
          (isBaseDataProviderScentre && this.isTileSelected(DemographicTile.BASE_DEMOGRAPHICS))
        ) {
          const selectedScentreDemographics = [...this.selectedScentrePredefinedSegments];
          if (isBaseDataProviderScentre) {
            selectedScentreDemographics.push(...this.baseDemographics);
          }
          const combination = ScentreUtils.findCombination(
            this.scentreDemographicsCombinations,
            selectedScentreDemographics,
          );
          this.selectedScentreCombination = combination ? [combination] : [];
        }

        switch (this.selectedTiles[0].id) {
          case DemographicTile.AUDIENCE_SEGMENTS:
          case DemographicTile.LIFESIGHT_DEMOGRAPHICS:
            if (this.outsourcedDemographics.length > 0) {
              this.$emit('changed', {
                type: 'outsourced',
                desc: this.selectedOutsourcedSegments.map((x) => x.name).join(', '),
                value: this.selectedOutsourcedSegments,
              });
            } else if (this.selectedLifesightSegments?.length) {
              this.$emit('changed', {
                type: 'segment',
                desc: this.selectedLifesightSegments.map((x) => x.name).join(', '),
                value: this.selectedLifesightSegments,
              });
            } else {
              this.$emit('changed', {
                type: 'segment',
                desc: this.selectedPredefinedSegments.map((x) => x.name).join(', '),
                value: this.selectedPredefinedSegments,
              });
            }
            break;
          case DemographicTile.CUSTOM_AUDIENCE: {
            const type = [];
            let desc = DASH;

            if (this.selectedCustomDemographic != null && this.isCustomAudienceSelected) {
              type.push('custom');
              desc = desc.concat(this.selectedCustomDemographic.name);
            }

            this.$emit('changed', {
              type,
              desc,
            });
            break;
          }
          case DemographicTile.BASE_DEMOGRAPHICS:
            switch (this.baseDataProvider?.code) {
              case DataProviderCodes.KANTAR:
                let description = '';
                if (this.selectedKantarCombination?.length) {
                  description = (
                    this.selectedKantarCombination?.length
                      ? this.selectedKantarCombination
                      : this.selectedBaseDemographics
                  )
                    .map((x) => x.name)
                    .join(', ');
                }
                this.$emit('changed', {
                  type: 'custom',
                  desc: description,
                  value: this.selectedKantarCombination?.length
                    ? this.selectedKantarCombination
                    : this.selectedBaseDemographics,
                });
                break;
              case DataProviderCodes.SCENTRE:
                this.$emit('changed', {
                  type: 'predefined',
                  desc: (this.selectedScentreCombination?.length && this.selectedBaseDemographics?.length
                    ? this.selectedScentreCombination
                    : this.selectedBaseDemographics
                  )
                    .map((x) => x.name)
                    .join(', '),
                  value:
                    this.selectedScentreCombination?.length && this.selectedBaseDemographics?.length
                      ? this.selectedScentreCombination
                      : this.selectedBaseDemographics,
                });
                break;
              default:
                this.$emit('changed', {
                  type: 'age-gender-hhi',
                  desc: [
                    this.selectedBaseDemographics.find(DemographicUtils.isGenderDemographic)?.name,
                    this.selectedBaseDemographics
                      .filter(DemographicUtils.isAgeDemographic)
                      .map((x) => x.name)
                      .join('-'),
                    this.selectedBaseDemographics
                      .filter(DemographicUtils.isIncomeDemographic)
                      .map((x) => x.name)
                      .join('-'),
                    this.selectedBaseDemographics
                      .filter(DemographicUtils.isEthnicityDemographic)
                      .map((x) => x.name)
                      .join(','),
                  ]
                    .filter((x) => !!x)
                    .join(', '),
                });
                break;
            }
            break;
          case DemographicTile.SCENTRE_PREDEFINED_SEGMENTS:
            this.$emit('changed', {
              type: 'predefined',
              desc: (this.selectedScentreCombination?.length && this.selectedScentrePredefinedSegments?.length
                ? this.selectedScentreCombination
                : this.selectedScentrePredefinedSegments
              )
                .map((x) => x.name)
                .join(', '),
              value:
                this.selectedScentreCombination?.length && this.selectedScentrePredefinedSegments?.length
                  ? this.selectedScentreCombination
                  : this.selectedScentrePredefinedSegments,
            });
            break;
          case DemographicTile.AUDIENCE_FILES:
            this.$emit('changed', {
              type: 'custom',
              desc: this.selectedAudienceFile != null ? this.selectedAudienceFile.name : DASH,
              value: this.selectedAudienceFile,
            });
            break;
          default:
            this.$emit('changed', {
              type: 'custom',
              desc: this.selectedCustomDemographic != null ? this.selectedCustomDemographic.name : DASH,
              value: this.selectedCustomDemographic,
            });
            break;
        }
      },
      updateBetaDemos() {
        if (!this.isReady || this.isReadOnly) {
          return;
        }
        if (
          this.membershipSettings != null &&
          this.membershipSettings.uiEnableDemoFeatures &&
          this.userHasAccessToDemographics &&
          this.selectedTiles.length > 0
        ) {
          switch (this.selectedTiles[0].id) {
            case DemographicTile.AUDIENCE_SEGMENTS:
              this.onUpdateBetaDemos(
                JSON.stringify({
                  tile: DemographicTile.AUDIENCE_SEGMENTS,
                  selected: this.selectedPredefinedSegments.map((x) => x.name),
                }),
              );
              break;
            case DemographicTile.BASE_DEMOGRAPHICS:
              if (this.$refs.censusDemographics) {
                this.onUpdateBetaDemos(this.$refs.censusDemographics.updateBetaDemos());
              }
              break;
            default:
              break;
          }
        }
      },
      onUpdateBetaDemos(state) {
        let entityProperty = {
          key: 'beta-audience',
          value: state,
        };
        if (this.isLineItem) {
          entityProperty = new LineItemProperty(null, null, 'beta-audience', state);
        } else if (this.isPackage) {
          entityProperty = new PackageProperty(null, null, 'beta-audience', state);
        } else if (
          this.isPlan &&
          (this.isTileSelected(DemographicTile.AUDIENCE_SEGMENTS) ||
            this.isTileSelected(DemographicTile.BASE_DEMOGRAPHICS))
        ) {
          // Plans only support max of 200 characters in Plan properties.
          entityProperty = new PlanProperty(null, null, 'beta-audience', state);
        }
        const entityProperties = [...this.entityProperties.filter((x) => x.key !== 'beta-audience')];
        if (state != null) {
          entityProperties.push(entityProperty);
        }
        this.updateEntityProperties(entityProperties);
        this.$emit('propertiesChanged', entityProperties);
      },
      updateOutsourcedSegments() {
        if (this.selectedOutsourcedSegments != null && this.selectedOutsourcedSegments.length > 0) {
          this.updateEntityDemographicIDs(this.selectedOutsourcedSegments.map((x) => x.id));
        }
      },
      toggleOverridable() {
        this.updateEntityDemographicIDs([]);
        this.updateEntityProperties([...this.entityProperties.filter((x) => x.key !== 'beta-audience')]);
        this.$emit('toggled');
      },
      onLifesightDemographicSelected({ segments }) {
        if (segments?.length) {
          this.selectedLifesightSegments = segments;
          this.updateEntityDemographicIDs(segments?.length ? segments.map((segment) => segment.demographicId) : []);
          this.fireChangeEvent();
        }
      },
      onPrizmSegmentSelected(demographic) {
        this.updateEntityDemographicIDs(demographic?.id ? [demographic.id] : []);
        this.fireChangeEvent();
      },
      onBaseDemographicsSelected(demographics) {
        this.$emit(GlobalEvents.error, null);
        let selectedDemographics = [];

        // DP specific logic
        if (this.baseDataProvider) {
          switch (this.baseDataProvider.code) {
            case DataProviderCodes.SCENTRE:
              if (this.selectedScentrePredefinedSegments?.length) {
                selectedDemographics = [...this.selectedScentrePredefinedSegments];
              }
              break;
            case DataProviderCodes.KANTAR:
              const combination = KantarUtils.findCombination(
                this.kantarDemographicsCombinations,
                this.selectedBaseDemographics,
              );
              this.selectedKantarCombination = combination ? [combination] : [];
              if (this.selectedKantarCombination.length) selectedDemographics = this.selectedKantarCombination;
              break;
            default:
              if (this.selectedCustomDemographic != null) {
                selectedDemographics.push(this.selectedCustomDemographic);
              }
              break;
          }
        }

        if (demographics?.length) {
          selectedDemographics.push(...demographics);
        }

        this.selectedBaseDemographics = demographics;
        this.updateEntityDemographicIDs(selectedDemographics?.length ? selectedDemographics.map((d) => d.id) : []);
        this.fireChangeEvent();
      },
      onScentrePredefinedSegmentChange(demographic) {
        this.$emit(GlobalEvents.error, null);
        let selectedDemographics = [];
        if (this.selectedBaseDemographics?.length) {
          selectedDemographics = [...this.selectedBaseDemographics];
        }

        if (demographic) {
          selectedDemographics.push(demographic);
        }

        this.selectedScentrePredefinedSegments = demographic ? [demographic] : [];
        this.updateEntityDemographicIDs(selectedDemographics?.length ? selectedDemographics.map((d) => d.id) : []);
        this.fireChangeEvent();
      },
      onSelectedAudienceTilesChanged(tiles) {
        if (tiles.length === 1 && this.selectedTiles.length === 1 && this.selectedTiles.at(0).id === tiles.at(0).id) {
          this.selectedTiles = [];
          return;
        }

        this.canMultiSelectAudienceSegments = tiles.every(
          (tile) =>
            tile.id === DemographicTile.BASE_DEMOGRAPHICS ||
            tile.id === DemographicTile.CUSTOM_AUDIENCE ||
            tile.id === DemographicTile.SCENTRE_PREDEFINED_SEGMENTS,
        );
        const lastClicked = last(tiles);

        const lastSelectedWasScentre =
          lastClicked?.id === DemographicTile.SCENTRE_PREDEFINED_SEGMENTS ||
          (lastClicked?.id === DemographicTile.BASE_DEMOGRAPHICS &&
            this.baseDataProvider?.code === DataProviderCodes.SCENTRE);
        if (this.canMultiSelectAudienceSegments) {
          this.selectedTiles = lastSelectedWasScentre
            ? tiles.filter(
                (tile) =>
                  tile.id === DemographicTile.BASE_DEMOGRAPHICS ||
                  tile.id === DemographicTile.SCENTRE_PREDEFINED_SEGMENTS,
              )
            : tiles.filter(
                (tile) => tile.id === DemographicTile.BASE_DEMOGRAPHICS || tile.id === DemographicTile.CUSTOM_AUDIENCE,
              );
        } else {
          // we can't select several types of audiences. The last tile clicked is the last one in the list. Only select this one
          this.selectedTiles = lastSelectedWasScentre
            ? [
                last(
                  tiles.filter(
                    (tile) =>
                      tile.id !== DemographicTile.BASE_DEMOGRAPHICS &&
                      tile.id !== DemographicTile.SCENTRE_PREDEFINED_SEGMENTS,
                  ),
                ),
              ]
            : [
                last(
                  tiles.filter(
                    (tile) =>
                      tile.id !== DemographicTile.BASE_DEMOGRAPHICS && tile.id !== DemographicTile.CUSTOM_AUDIENCE,
                  ),
                ),
              ];
        }

        // In edit view resetting the value when one tile is unselected (only when two tiles are already selected)
        if (tiles?.length === 1) {
          switch (tiles[0].id) {
            case DemographicTile.BASE_DEMOGRAPHICS:
              this.selectedAudienceFile = null;
              this.selectedCustomDemographic = null;
              this.selectedImportedAudienceSegments = null;
              this.updateEntityDemographicIDs(this.selectedBaseDemographics.map((d) => d.id));
              break;
            case DemographicTile.CUSTOM_AUDIENCE:
              this.selectedBaseDemographics = [];
              this.selectedAudienceFile = null;
              this.selectedImportedAudienceSegments = null;
              this.updateEntityDemographicIDs(
                this.selectedCustomDemographic ? [this.selectedCustomDemographic.id] : [],
              );
              break;
            case DemographicTile.AUDIENCE_FILES:
              this.selectedBaseDemographics = [];
              this.selectedCustomDemographic = null;
              this.selectedImportedAudienceSegments = null;
              this.updateEntityDemographicIDs([]);
              break;
            case DemographicTile.IMPORTED_AUDIENCES:
              this.selectedBaseDemographics = [];
              this.selectedCustomDemographic = null;
              this.selectedAudienceFile = null;
              this.updateEntityDemographicIDs([]);
              break;
            default:
              break;
          }
        }
      },
      async restoreDemographics() {
        this.entityHasUnknownDemographics = false;
        if (this.entity != null && this.entity.demographicIDs?.length > 0) {
          if (this.demographics.find((d) => d.id === this.entity.demographicIDs[0]) == null) {
            this.entityHasUnknownDemographics = true;
          } else if (this.demographics.find((d) => d.id === this.entity.demographicIDs[0]).type === 'OUTSOURCED') {
            if (
              AudienceSegmentMapping.some(
                (asm) => asm.code === this.demographics.find((d) => d.id === this.entity.demographicIDs[0]).code,
              )
            ) {
              //
              // Outsourced segments
              //
              this.selectedOutsourcedSegments = this.outsourcedDemographics.filter((d) =>
                this.entity.demographicIDs.includes(d.id),
              );
              this.selectedTiles = [this.tiles.find((t) => t.id === DemographicTile.AUDIENCE_SEGMENTS)];
            } else {
              //
              // Imported audiences
              //
              this.selectedImportedAudienceSegments = this.importedAudiences.find((d) =>
                this.entity.demographicIDs.includes(d.id),
              );
              this.selectedTiles = [this.tiles.find((t) => t.id === DemographicTile.IMPORTED_AUDIENCES)];
            }
          } else if (this.demographics.find((d) => d.id === this.entity.demographicIDs[0]).type === 'AUDIENCE-FILE') {
            //
            // Audience files
            //
            this.selectedAudienceFile = this.demographics.find((d) => d.id === this.entity.demographicIDs[0]);
            this.selectedTiles = [this.tiles.find((t) => t.id === DemographicTile.AUDIENCE_FILES)];
          } else {
            const demos2 = this.demographics.filter((d) => this.entity.demographicIDs.includes(d.id));
            this.isCustomAudienceSelected = demos2.some(
              (x) =>
                x.type === DemographicType.Custom &&
                x.subType !== DemographicType.Gender &&
                x.subType !== DemographicType.Age &&
                !x.code.startsWith(AudienceSegmentUtils.prizmId),
            );
            const isBaseDemographicsSelected = demos2.some(DemographicUtils.isBaseDemographic);
            const isLifesightPredefinedDemosSelected = demos2.some(
              (x) =>
                x.type === DemographicType.Custom &&
                (x.subType === DemographicType.Gender || x.subType === DemographicType.Age) &&
                x.code.startsWith('LSD'),
            );
            const isPrizmSegmentsSelected = demos2.some(
              (x) => x.type === DemographicType.Custom && x.code.startsWith(AudienceSegmentUtils.prizmId),
            );
            const isScentrePredefinedSegmentsSelected = demos2.some(
              (x) => ScentreUtils.isPredefinedSegment(x, this.organization) && ScentreUtils.isPrimaryDemographic(x),
            );
            const isScentreCombinationSelected = demos2.some(
              (x) => ScentreUtils.isScentreDemographics(x, this.organization) && ScentreUtils.isCombination(x),
            );
            let isKantarCombinationSelected = false;
            let kantarDemographicsFromEntityIDs = [];
            if (this.baseDataProvider?.code === DataProviderCodes.KANTAR) {
              kantarDemographicsFromEntityIDs = KantarUtils.getKantarDemographics(demos2, this.kantarDataProvider.id);
              isKantarCombinationSelected = kantarDemographicsFromEntityIDs.some((x) => KantarUtils.isCombination(x));
            }

            if (this.isCustomAudienceSelected && !isKantarCombinationSelected) {
              //
              // Custom Audience
              //
              [this.selectedCustomDemographic] = this.demographics.filter(
                (x) => x.type === DemographicType.Custom && this.entity.demographicIDs.includes(x.id),
              );
              this.selectedTiles = [this.tiles.find((t) => t.id === DemographicTile.CUSTOM_AUDIENCE)];
            }

            if (isBaseDemographicsSelected) {
              this.selectedBaseDemographics = this.demographics.filter(
                (demo) => this.entity.demographicIDs.indexOf(demo.id) > -1 && DemographicUtils.isBaseDemographic(demo),
              );
              this.selectedTiles = [
                ...this.selectedTiles,
                this.tiles.find((t) => t.id === DemographicTile.BASE_DEMOGRAPHICS),
              ];
            }

            if (isLifesightPredefinedDemosSelected) {
              const query = new QueryParams(
                demos2.length,
                0,
                new FilterCollection([
                  new Filter(
                    'demographic_id',
                    demos2.map((d) => d.id),
                    FilterComparisonOperator.EQUAL,
                  ),
                ]),
              );
              query.customQueryParams.dataProviderId = this.lifesightDataProvider.id;
              const res = await query.run(this.getAllLifesightSegments);
              this.selectedLifesightSegments = res.items;
              this.selectedTiles = [this.tiles.find((t) => t.id === DemographicTile.LIFESIGHT_DEMOGRAPHICS)];
            }

            if (isPrizmSegmentsSelected) {
              this.selectedPrizmSegments = demos2.filter(
                (d) => d.type === DemographicType.Custom && d.code.startsWith(AudienceSegmentUtils.prizmId),
              );
              this.selectedTiles = [this.tiles.find((t) => t.id === DemographicTile.PRIZM_SEGMENTS)];
            }

            if (isScentreCombinationSelected) {
              const primaries = ScentreUtils.getPrimaries(this.demographics, this.organization);
              const combination = demos2.find(
                (d) => ScentreUtils.isScentreDemographics(d, this.organization) && ScentreUtils.isCombination(d),
              );
              const primaryDemographics = ScentreUtils.getPrimariesFromCombination(primaries, combination);
              const scentreDemographics = primaryDemographics.filter((d) =>
                ScentreUtils.isAgeAndGenderDemographic(d, this.organization),
              );
              const scentrePredefinedSegment = primaryDemographics.filter((d) =>
                ScentreUtils.isPredefinedSegment(d, this.organization),
              );

              if (combination) {
                this.selectedScentreCombination = [combination];
              }

              if (scentreDemographics?.length) {
                this.selectedTiles = [this.tiles.find((t) => t.id === DemographicTile.BASE_DEMOGRAPHICS)];
              }

              if (scentrePredefinedSegment?.length) {
                this.selectedScentrePredefinedSegments = scentrePredefinedSegment;
                // We push instead of assigning to avoid overwriting the previous tile because the user can select both demographics and predefined segments for scentre.
                this.selectedTiles.push(this.tiles.find((t) => t.id === DemographicTile.SCENTRE_PREDEFINED_SEGMENTS));
              }
            }

            if (isScentrePredefinedSegmentsSelected) {
              this.selectedScentrePredefinedSegments = demos2.filter((d) =>
                ScentreUtils.isPredefinedSegment(d, this.organization),
              );
              this.selectedTiles.push(this.tiles.find((t) => t.id === DemographicTile.SCENTRE_PREDEFINED_SEGMENTS));
            }

            if (isKantarCombinationSelected) {
              this.selectedKantarCombination = this.baseDemographics.filter((x) => KantarUtils.isCombination(x));

              // We will need to find the primaries from the combination so that the current demographics are selected when entering the edit page.
              const uniqueCodes = [];
              this.selectedKantarCombination.forEach((demo) => {
                uniqueCodes.push(...KantarUtils.getCodesFromCombinationCode(demo));
              });
              uniqueCodes.forEach((code) => {
                this.selectedBaseDemographics.push(this.baseDemographics.find((demo) => demo.code === `std: ${code}`));
              });
            }
          }

          if (this.userHasAccessToDemographics && this.tiles.length === 1 && this.selectedTiles == null) {
            this.selectedTiles = this.tiles;
          }
          if (this.selectedTiles.length > 0) {
            this.canMultiSelectAudienceSegments = this.selectedTiles.every(
              (tile) =>
                tile?.id === DemographicTile.BASE_DEMOGRAPHICS ||
                tile?.id === DemographicTile.CUSTOM_AUDIENCE ||
                tile?.id === DemographicTile.SCENTRE_PREDEFINED_SEGMENTS,
            );
          }
          this.isReady = true;
        } else {
          if (this.tiles.length > 0) {
            this.selectedTiles = [];
            this.canMultiSelectAudienceSegments = this.selectedTiles.every(
              (tile) =>
                tile.id === DemographicTile.BASE_DEMOGRAPHICS ||
                tile.id === DemographicTile.CUSTOM_AUDIENCE ||
                tile.id === DemographicTile.SCENTRE_PREDEFINED_SEGMENTS,
            );
          }
          this.isReady = true;
        }
      },
      setOriginalDemographics() {
        this.originalDemographicIDs = this.entity.demographicIDs;
        if (this.isLineItem) {
          this.originalProperties = this.entity.lineItemProperties;
        } else if (this.isPackage) {
          this.originalProperties = this.entity.packageProperties;
        } else if (this.isPlan) {
          this.originalProperties = this.entity.planProperties;
        }
      },
      async initialize() {
        this.dataProviders = await this.getAllDataProviders();
        this.didStartInitialization = true;
        this.isLoadingDemographics = true;
        await waitForPendingQueries([this.demographicsQuery]);
        this.isLoadingDemographics = false;

        //
        // Restore selected demographics
        //
        await this.restoreDemographics();

        // Adding a check to see if anything else is selected, only apply this if not
        if (this.hasBetaAudience && !this.selectedTiles.length) {
          try {
            const state = JSON.parse(this.entityProperties.find((x) => x.key === 'beta-audience').value);
            if (state.tile != null || state.tab != null) {
              const tileID = state.tab != null ? state.tab : state.tile;
              const getTileID = (id) => id.replace('tab_', '').replace('tile_', '');
              const selectedTile = this.tiles.find((x) => getTileID(x.id) === getTileID(tileID));
              if (selectedTile) {
                this.selectedTiles = [selectedTile];
                this.outdatedAudienceWarningMessage = null;
              } else {
                this.selectedTiles = [];
                const tileName = getTileID(tileID).replace('_', ' ');
                this.outdatedAudienceWarningMessage = i18n.global.t(
                  'messages.audienceIsNoLongerAvailableForTheOrganization',
                  [tileName],
                );
                const newEntityProperties = this.entityProperties.filter((x) => x.key !== 'beta-audience');
                this.updateEntityProperties(newEntityProperties);
              }

              switch (tileID) {
                case DemographicTile.AUDIENCE_SEGMENTS:
                  this.selectedPredefinedSegments = this.predefinedSegments.filter((x) =>
                    state.selected.find((y) => y === x.name),
                  );
                  break;
                default:
                  break;
              }
            }
          } catch (err) {
            console.log(err);
          }
        }
      },
      editCustomAudience() {
        if (this.selectedCustomAudience) {
          this.trackQuickCustomAudience(this.selectedCustomAudience.id);
          this.isCustomAudienceModalForNewAudience = false;
          this.isCustomAudienceModalShowing = true;
        }
      },
      addCustomAudience() {
        this.trackQuickCustomAudience();
        this.isCustomAudienceModalForNewAudience = true;
        this.isCustomAudienceModalShowing = true;
      },
      trackQuickCustomAudience(id = null) {
        const value = id ?? 'new';

        GoogleAnalyticsApi.sendEvent('quick_audience', { category: 'EDIT', value });
      },
      async updateCustomAudience(dataProviderId) {
        this.isCustomAudienceModalShowing = false;
        // Update demographics since we added a new one
        if (dataProviderId) {
          this.isLoadingDemographics = true;
          await this.demographicsQuery.query.refetch();
          this.isLoadingDemographics = false;
          this.selectedCustomDemographic = this.demographics.find((d) => d.id === dataProviderId);
        }
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .warning-custom-audience {
    display: flex;
    stroke: var(--actionsColorLighter);
    color: var(--actionsColorLighter);
  }

  .summary-custom-audience {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;

    .row {
      width: 100%;
    }
  }

  #edit_lineitem_demographics {
    .form-group.row {
      width: 100%;
      margin-left: 0;
      margin-right: 0;
    }
  }

  .target-custom-audience {
    display: flex;
    gap: 1rem;
  }

  .slider-container {
    display: flex;
    flex-direction: row;
    align-items: center;

    svg {
      height: 4.8rem;
      width: 4.8rem;

      &:first-of-type {
        margin-right: 2.4rem;
      }

      &:last-of-type {
        margin-left: 2.4rem;
      }
    }

    .vue-slider {
      flex-grow: 1;
      margin-right: 3.2rem;
    }
  }

  .section-label {
    text-transform: uppercase;
    font-weight: 100;
    font-size: 0.8em;
    margin-bottom: 0.6rem;
  }

  .well {
    padding: 2.4rem 3.2rem;
  }

  #app #edit_lineitem_demographics .alert {
    margin-top: 0;
  }
</style>
